/**
 * Video Synchronization Utilities
 *
 * These utilities handle the synchronization of video elements with the timeline's
 * currentPlayingTime. Videos can have positive delays (start after timeline 0),
 * negative delays (pre-rolled before timeline starts), and trim offsets.
 */

/**
 * Calculates the video currentTime (in seconds) based on the timeline's current playing time
 *
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @param {number} elementDelay - Element delay in milliseconds (can be negative)
 * @param {number} startOffset - Video trim start offset in milliseconds
 * @param {number} trimDuration - Video trim duration in milliseconds (optional)
 * @returns {number} Video currentTime in seconds
 *
 * @example
 * // Video with no delay, no trim
 * calculateVideoTimeFromTimeline(2000, 0, 0) // returns 2.0
 *
 * @example
 * // Video with positive delay (starts at timeline 1s)
 * calculateVideoTimeFromTimeline(2000, 1000, 0) // returns 1.0
 *
 * @example
 * // Video with negative delay (pre-rolled by 500ms)
 * calculateVideoTimeFromTimeline(0, -500, 0) // returns 0.5
 *
 * @example
 * // Video with trim offset (video starts at 2s mark)
 * calculateVideoTimeFromTimeline(1000, 0, 2000) // returns 3.0
 */
export const calculateVideoTimeFromTimeline = (
  currentPlayingTime,
  elementDelay = 0,
  startOffset = 0,
  trimDuration = null
) => {
  // Calculate the video's elapsed time relative to when it should start playing
  // Positive delay: video hasn't started yet when timeline is before delay
  // Negative delay: video has been "playing" before timeline started
  const videoElapsedTime = currentPlayingTime - elementDelay;

  // Add the trim offset to get the actual position in the video file
  const videoTimeMs = startOffset + videoElapsedTime;

  // Convert to seconds
  const videoTimeSeconds = videoTimeMs / 1000;

  // Clamp to non-negative values (can't seek before start of video)
  const clampedTime = Math.max(0, videoTimeSeconds);

  // If trimDuration is specified, clamp to the trim end
  if (trimDuration !== null) {
    const trimEndSeconds = (startOffset + trimDuration) / 1000;
    return Math.min(clampedTime, trimEndSeconds);
  }

  return clampedTime;
};

/**
 * Determines if a video should be visible based on the timeline's current time
 *
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @param {number} elementDelay - Element delay in milliseconds (can be negative)
 * @param {number} elementDuration - Total duration the element should be visible in milliseconds
 * @returns {boolean} True if video should be visible
 *
 * @example
 * // Video with delay=1000ms, duration=3000ms
 * shouldVideoBeVisible(500, 1000, 3000)  // false (before delay)
 * shouldVideoBeVisible(2000, 1000, 3000) // true (1s into playback)
 * shouldVideoBeVisible(5000, 1000, 3000) // false (after duration)
 */
export const shouldVideoBeVisible = (
  currentPlayingTime,
  elementDelay = 0,
  elementDuration
) => {
  // Video should be visible when timeline is at or after the delay
  // and before the delay + duration
  const videoStartTime = elementDelay;
  const videoEndTime = elementDelay + elementDuration;

  return (
    currentPlayingTime >= videoStartTime && currentPlayingTime < videoEndTime
  );
};

/**
 * Determines the video playback state based on timeline state and timing
 *
 * @param {boolean} isTimelinePlaying - Whether the timeline is currently playing
 * @param {boolean} isTimelinePaused - Whether the timeline is currently paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @param {number} elementDelay - Element delay in milliseconds
 * @param {number} elementDuration - Total element duration in milliseconds
 * @returns {"playing" | "paused" | "hidden"} The video playback state
 *
 * @example
 * // Timeline playing, video in active range
 * getVideoPlaybackState(true, false, 2000, 1000, 3000) // "playing"
 *
 * @example
 * // Timeline paused, video in active range
 * getVideoPlaybackState(false, true, 2000, 1000, 3000) // "paused"
 *
 * @example
 * // Timeline playing, video not yet started
 * getVideoPlaybackState(true, false, 500, 1000, 3000) // "hidden"
 */
export const getVideoPlaybackState = (
  isTimelinePlaying,
  isTimelinePaused,
  currentPlayingTime,
  elementDelay = 0,
  elementDuration
) => {
  const isVisible = shouldVideoBeVisible(
    currentPlayingTime,
    elementDelay,
    elementDuration
  );

  // If video is not in its visible range, it should be hidden
  if (!isVisible) {
    return "hidden";
  }

  // If timeline is playing and video is visible, video should play
  if (isTimelinePlaying) {
    return "playing";
  }

  // If timeline is paused and video is visible, video should pause
  if (isTimelinePaused) {
    return "paused";
  }

  // Timeline is stopped (not playing, not paused) - video should be hidden
  return "hidden";
};

/**
 * Checks if a video seek is necessary based on current and target times
 * This prevents excessive seeking during rapid timeline updates
 *
 * @param {number} currentVideoTime - Current video.currentTime in seconds
 * @param {number} targetVideoTime - Target video time in seconds
 * @param {number} threshold - Threshold in seconds for considering seek necessary (default 0.1s)
 * @returns {boolean} True if seek is necessary
 */
export const shouldSeekVideo = (
  currentVideoTime,
  targetVideoTime,
  threshold = 0.1
) => {
  return Math.abs(currentVideoTime - targetVideoTime) > threshold;
};
